"use client";

import { useEffect, useMemo, useState } from "react";
import { Folder } from "lucide-react";
import { WorkspaceImage } from "@/app/components/WorkspaceImage";
import {
    DEFAULT_WORKSPACE_AVATAR,
    DEFAULT_WORKSPACE_ICON_SURFACE,
    resolveWorkspaceAvatarFromWorkspace,
    resolveWorkspaceIconComponent,
    resolveWorkspaceImageDisplayUrl,
    type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";

type WorkspaceSidebarIconProps = {
    workspace: {
        id?: string;
        name?: string;
        imageUrl?: string | null;
        tags?: Array<{ key?: string; value?: string }> | null;
    };
    active: boolean;
};

const ICON_TRANSITION_CLASS =
    "transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none";

function isDefaultAvatar(avatar: WorkspaceAvatarSelection): boolean {
    return (
        avatar.mode === "icon" &&
        avatar.iconKey === DEFAULT_WORKSPACE_AVATAR.iconKey &&
        avatar.color === DEFAULT_WORKSPACE_AVATAR.color
    );
}

export function WorkspaceSidebarIcon({ workspace, active }: WorkspaceSidebarIconProps) {
    const [imageFailed, setImageFailed] = useState(false);

    const avatar = useMemo(
        () =>
            resolveWorkspaceAvatarFromWorkspace({
                imageUrl: workspace.imageUrl,
                tags: workspace.tags,
                resolveImageUrl: resolveWorkspaceImageDisplayUrl,
            }),
        [workspace.imageUrl, workspace.tags],
    );

    useEffect(() => {
        setImageFailed(false);
    }, [workspace.id, workspace.imageUrl]);

    if (avatar.mode === "image" && avatar.preview?.trim() && !imageFailed) {
        return (
            <div
                className={`absolute inset-0 overflow-hidden rounded-xl bg-aera-white ${ICON_TRANSITION_CLASS} ${
                    active
                        ? "ring-2 ring-aera-charcoal ring-inset"
                        : "group-hover:brightness-[1.04]"
                }`}
            >
                <WorkspaceImage
                    src={avatar.preview}
                    alt={workspace.name ?? ""}
                    className="h-full w-full object-cover"
                    onError={() => setImageFailed(true)}
                />
            </div>
        );
    }

    if (avatar.mode === "icon" || imageFailed) {
        const iconAvatar =
            avatar.mode === "icon" ? avatar : DEFAULT_WORKSPACE_AVATAR;
        const isDefault = isDefaultAvatar(iconAvatar);
        const Icon = isDefault ? Folder : resolveWorkspaceIconComponent(iconAvatar.iconKey);
        const surfaceColor = active
            ? "transparent"
            : isDefault
              ? DEFAULT_WORKSPACE_ICON_SURFACE
              : `${iconAvatar.color}18`;

        return (
            <div
                className={`absolute inset-0 rounded-xl flex items-center justify-center ${ICON_TRANSITION_CLASS} ${
                    active ? "" : "group-hover:brightness-[1.04]"
                }`}
                style={{ backgroundColor: surfaceColor }}
            >
                <Icon
                    size={22}
                    strokeWidth={2}
                    className={
                        active
                            ? "text-aera-white"
                            : isDefault
                              ? "text-aera-charcoal"
                              : undefined
                    }
                    style={
                        active || isDefault
                            ? undefined
                            : { color: iconAvatar.color }
                    }
                />
            </div>
        );
    }

    return (
        <div
            className={`absolute inset-0 rounded-xl flex items-center justify-center ${ICON_TRANSITION_CLASS} ${
                active ? "" : "group-hover:brightness-[1.04]"
            }`}
            style={{
                backgroundColor: active
                    ? "transparent"
                    : DEFAULT_WORKSPACE_ICON_SURFACE,
            }}
        >
            <Folder
                size={22}
                className={active ? "text-aera-white" : "text-aera-charcoal"}
                strokeWidth={2}
            />
        </div>
    );
}
